/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import type { SupplierCompanyProfile } from '../../types/types';
import SupplierProfileBuilderCountryBadge from './SupplierProfileBuilderCountryBadge';

interface SupplierProfileBuilderOperationsTabProps {
  operations: any;
  setOperations: React.Dispatch<React.SetStateAction<any>>;
  profile: SupplierCompanyProfile;
  setProfile: React.Dispatch<React.SetStateAction<SupplierCompanyProfile>>;
  opCountries: number[];
  countries: { id: number; name: string; code: string }[];
  tempCountry: number | null;
  setTempCountry: React.Dispatch<React.SetStateAction<number | null>>;
  errors: { [key: string]: string };
  isSaving: boolean;
  addCountry: () => void;
  removeCountry: (id: number) => void;
  onSave: () => void;
}

const SupplierProfileBuilderOperationsTab: React.FC<
  SupplierProfileBuilderOperationsTabProps
> = ({
  operations,
  setOperations,
  profile,
  setProfile,
  opCountries,
  countries,
  tempCountry,
  setTempCountry,
  errors,
  isSaving,
  addCountry,
  removeCountry,
  onSave,
}) => {
  return (
    <Form className="p-3">
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Employee Count</Form.Label>
            <Form.Control
              type="number"
              value={operations.employee_count || ''}
              onChange={(e) =>
                setOperations({
                  ...operations,
                  employee_count: e.target.value,
                })
              }
            />
            {errors.employee_count && (
              <Form.Text className="text-danger">
                {errors.employee_count}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Revenue Range</Form.Label>
            <Form.Select
              value={operations.revenue_range || ''}
              onChange={(e) =>
                setOperations({
                  ...operations,
                  revenue_range: e.target.value,
                })
              }
            >
              <option value="">Select Range...</option>
              <option value="0-1M">$0 - $1M</option>
              <option value="1M-10M">$1M - $10M</option>
              <option value="10M+">$10M+</option>
            </Form.Select>
            {errors.revenue_range && (
              <Form.Text className="text-danger">
                {errors.revenue_range}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Add Countries of Operation</Form.Label>
            <Row>
              <Col md={10}>
                <select
                  className="form-select"
                  value={tempCountry || ''}
                  onChange={(e) => setTempCountry(Number(e.target.value))}
                >
                  <option value="">Select a country...</option>
                  {countries
                    .filter((c) => !opCountries.includes(c.id))
                    .map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                </select>
                <label className="form-text">
                  Select a country and click "Add" to include it in your
                  profile.
                </label>
              </Col>
              <Col md={2}>
                <Button
                  variant={!tempCountry ? 'outline-primary' : 'primary'}
                  onClick={addCountry}
                  disabled={!tempCountry}
                >
                  Add
                </Button>
              </Col>
            </Row>
            {errors.opCountries && opCountries.length === 0 && (
              <Form.Text className="text-danger">
                {errors.opCountries}
              </Form.Text>
            )}
          </Form.Group>

          <div className="mb-4 d-flex flex-wrap gap-2">
            {opCountries.map((countryId) => (
              <SupplierProfileBuilderCountryBadge
                key={countryId}
                countryId={countryId}
                countryName={
                  countries.find((c) => c.id === countryId)?.name ||
                  countryId.toString()
                }
                removeCountry={removeCountry}
              />
            ))}
          </div>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Year Founded</Form.Label>
            <Form.Control
              type="text"
              value={profile?.founded_year || ''}
              onChange={(e) => {
                const year = parseInt(e.target.value, 10);
                setProfile((prev) => ({
                  ...prev,
                  founded_year: !isNaN(year) ? year : undefined,
                }));
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Button variant="primary" onClick={onSave} disabled={isSaving}>
        {isSaving ? 'Saving...' : 'Save & Next'}
      </Button>
    </Form>
  );
};

export default SupplierProfileBuilderOperationsTab;
